/**
 * New artifact: owner, title, description, release note and the file that
 * becomes version 1.
 *
 * Two entry points share this one dialog (f15.2 design D5):
 *
 * * **A node's Artifacts tab (f15.1)** passes `owners` -- the node itself, or
 *   (Client tab) the client and its active engagements (Q-f15.1-4).
 * * **The top-level Artifacts screen (f15.2)** passes `levels` instead. The
 *   first section, Placement, asks for the owning level and then the node,
 *   filled from `GET /api/artifacts/owner-options`. Content stays disabled
 *   until a node is chosen, and changing the level clears the node and the
 *   file (BR-F15.2-04). There is no default owner.
 *
 * Either way the post is f15.1's `POST /api/artifacts`, and the server
 * re-authorizes against the chosen owner: the picker is never the boundary.
 */

import { useState } from 'react';
import { Upload } from 'lucide-react';

import { ApiError, type ArtifactOwnerType } from '@/api/client';
import { generalMessage, problemFor } from '@/api/fieldErrors';
import { useArtifactOwnerOptions, useCreateArtifact } from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';

import { levelLabel } from './artifactView';
import { FileDropzone } from './FileDropzone';

export interface OwnerOption {
  type: ArtifactOwnerType;
  id: string;
  label: string;
}

const FIELD_KEYS = ['owner_type', 'owner_id', 'title', 'description', 'release_note', 'file'];

/** Reason codes the server answers without a field key, and the control they belong to. */
const FILE_REASONS = new Set(['ARTIFACT_FORMAT_NOT_SUPPORTED', 'UPLOAD_TOO_LARGE', 'ARTIFACT_TYPE_MISMATCH']);
const OWNER_REASONS = new Set(['OWNER_INACTIVE', 'ROLE_NOT_PERMITTED']);

function reasonTarget(error: unknown): 'file' | 'owner' | null {
  if (!(error instanceof ApiError)) return null;
  if (error.reason && FILE_REASONS.has(error.reason)) return 'file';
  if ((error.reason && OWNER_REASONS.has(error.reason)) || error.status === 404) return 'owner';
  return null;
}

export function optionLabel(option: { name: string; code: string; parent?: { name: string } | null }): string {
  return option.parent ? `${option.name} · ${option.code} (${option.parent.name})` : `${option.name} · ${option.code}`;
}

function Placement({
  levels,
  level,
  ownerId,
  onLevel,
  onOwner,
  problem,
}: {
  levels: readonly ArtifactOwnerType[];
  level: ArtifactOwnerType | '';
  ownerId: string;
  onLevel: (level: ArtifactOwnerType | '') => void;
  onOwner: (option: OwnerOption | null) => void;
  problem: string | null;
}) {
  const options = useArtifactOwnerOptions(level);
  const nodes = options.data?.items ?? [];
  return (
    <fieldset className="mb-4">
      <div className="fieldset-title">Placement</div>
      <div className="grid grid-2">
        <DialogField label="Owning level" htmlFor="artifact-level" required>
          <select
            id="artifact-level"
            value={level}
            onChange={(event) => onLevel(event.target.value as ArtifactOwnerType | '')}
          >
            <option value="">Choose a level…</option>
            {levels.map((value) => (
              <option key={value} value={value}>
                {levelLabel(value)}
              </option>
            ))}
          </select>
        </DialogField>
        <DialogField
          label="Node"
          htmlFor="artifact-node"
          required
          problem={problem}
          hint="Visibility follows this node: whoever can see it can read the artifact"
        >
          <select
            id="artifact-node"
            value={ownerId}
            disabled={level === '' || options.isPending}
            onChange={(event) => {
              const chosen = nodes.find((node) => node.id === event.target.value);
              onOwner(
                chosen ? { type: chosen.type as ArtifactOwnerType, id: chosen.id, label: optionLabel(chosen) } : null,
              );
            }}
          >
            <option value="">{level === '' ? 'Choose a level first' : 'Choose a node…'}</option>
            {nodes.map((node) => (
              <option key={node.id} value={node.id}>
                {optionLabel(node)}
              </option>
            ))}
          </select>
        </DialogField>
      </div>
    </fieldset>
  );
}

export function NewArtifactDialog({
  owners,
  levels,
  onClose,
  onCreated,
}: {
  /** A fixed or constrained owner set (the node tabs); the first is the default. */
  owners?: readonly OwnerOption[];
  /** Owner-first placement (the top-level screen): the levels that have options. */
  levels?: readonly ArtifactOwnerType[];
  onClose: () => void;
  onCreated: (artifactId: string) => void;
}) {
  const create = useCreateArtifact();
  const placementMode = levels !== undefined;
  const fixed = owners ?? [];
  const [ownerKey, setOwnerKey] = useState(fixed[0] ? `${fixed[0].type}:${fixed[0].id}` : '');
  const [level, setLevel] = useState<ArtifactOwnerType | ''>('');
  const [placed, setPlaced] = useState<OwnerOption | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [releaseNote, setReleaseNote] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const owner = placementMode ? placed : fixed.find((option) => `${option.type}:${option.id}` === ownerKey);
  const contentLocked = !owner || create.isPending;
  const failure = create.error;
  const target = reasonTarget(failure);
  const ownerProblem =
    problemFor(failure, 'owner_type') ?? problemFor(failure, 'owner_id') ?? (target === 'owner' ? failure?.message ?? null : null);
  const fileProblem = problemFor(failure, 'file') ?? (target === 'file' ? failure?.message ?? null : null);

  function chooseLevel(next: ArtifactOwnerType | '') {
    // BR-F15.2-04: a new level is a new placement; nothing chosen for the old one survives.
    setLevel(next);
    setPlaced(null);
    setFile(null);
    create.reset();
  }

  function submit() {
    if (!owner || !file) return;
    create.mutate(
      {
        ownerType: owner.type,
        ownerId: owner.id,
        title,
        description: description.trim() || undefined,
        releaseNote: releaseNote.trim() || undefined,
        file,
      },
      { onSuccess: (created) => onCreated(created.id) },
    );
  }

  const content = (
    <>
      <DialogField label="Title" htmlFor="artifact-title" required problem={problemFor(failure, 'title')} hint="Up to 200 characters">
        <input
          id="artifact-title"
          type="text"
          value={title}
          required
          maxLength={200}
          disabled={contentLocked}
          placeholder="Q3 2026 Delivery Review"
          onChange={(event) => setTitle(event.target.value)}
        />
      </DialogField>
      <DialogField label="Description" htmlFor="artifact-description" problem={problemFor(failure, 'description')}>
        <textarea
          id="artifact-description"
          rows={3}
          maxLength={2000}
          value={description}
          disabled={contentLocked}
          placeholder="Shown to internal viewers"
          onChange={(event) => setDescription(event.target.value)}
        />
      </DialogField>
      {placementMode && (
        <div className="field">
          <label>Content type</label>
          <div className="hint">PDF or HTML, detected from the file</div>
        </div>
      )}
      <div className={`field${fileProblem ? ' has-problem' : ''}`}>
        <label className="req">Version 1 file</label>
        <FileDropzone file={file} onFile={setFile} disabled={contentLocked} />
        {fileProblem && (
          <span className="field-error" role="alert">
            {fileProblem}
          </span>
        )}
      </div>
      <DialogField label="Release note" htmlFor="artifact-note" problem={problemFor(failure, 'release_note')}>
        <input
          id="artifact-note"
          type="text"
          maxLength={500}
          value={releaseNote}
          disabled={contentLocked}
          placeholder="Initial publication"
          onChange={(event) => setReleaseNote(event.target.value)}
        />
      </DialogField>
    </>
  );

  return (
    <Modal
      title="New artifact"
      subtitle={
        placementMode
          ? 'An artifact can hang off any node in the hierarchy. Choose where first.'
          : 'The file becomes version 1. Every later upload is a new, immutable version.'
      }
      onClose={onClose}
      onSubmit={submit}
      busy={create.isPending}
      submitDisabled={!owner || !file || title.trim() === ''}
      submitLabel="Create artifact"
      submitIcon={<Upload aria-hidden="true" />}
      layout="plain"
      wide={placementMode}
      message={target ? null : generalMessage(failure, ...FIELD_KEYS)}
    >
      {placementMode ? (
        <>
          <Placement
            levels={levels}
            level={level}
            ownerId={placed?.id ?? ''}
            onLevel={chooseLevel}
            onOwner={(option) => {
              setPlaced(option);
              create.reset();
            }}
            problem={ownerProblem}
          />
          <fieldset aria-disabled={!owner}>
            <div className="fieldset-title">Content</div>
            {!owner && <p className="hint">Choose the owning node before adding the file.</p>}
            <div className="stack-sm">{content}</div>
          </fieldset>
        </>
      ) : (
        <div className="stack-sm">
          {fixed.length > 1 ? (
            <DialogField
              label="Hangs off"
              htmlFor="artifact-owner"
              problem={ownerProblem}
              hint="Visibility follows this node: whoever can see it can read the artifact"
            >
              <select id="artifact-owner" value={ownerKey} onChange={(event) => setOwnerKey(event.target.value)}>
                {fixed.map((option) => (
                  <option key={`${option.type}:${option.id}`} value={`${option.type}:${option.id}`}>
                    {option.label}
                  </option>
                ))}
              </select>
            </DialogField>
          ) : (
            owner && (
              <div className="field">
                <label>Hangs off</label>
                <div>{owner.label}</div>
                {ownerProblem && (
                  <span className="field-error" role="alert">
                    {ownerProblem}
                  </span>
                )}
              </div>
            )
          )}
          {content}
        </div>
      )}
    </Modal>
  );
}
